import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import MockAuctionBoard from './MockAuctionBoard';
import MobileBlockModal from '../MobileBlockModal';
import isMobileDevice from '../../utils/isMobileDevice';
import './LandingPage.css';

const HERO_BG = `url(${process.env.PUBLIC_URL}/assets/Auctioneer_On_Stage.png)`;

// ── Inline SVG icons (Lucide-style) ──
const Icon = {
  Mic: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3" />
      <path d="M8 21h8" />
    </svg>
  ),
  Timer: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l2.5 2.5" />
      <path d="M9 2h6" />
      <path d="M12 2v3" />
    </svg>
  ),
  Gavel: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m14 13-7.5 7.5a1.8 1.8 0 0 1-2.5-2.5L11.5 10.5" />
      <path d="m16 16 6-6" />
      <path d="m8 8 6-6" />
      <path d="m9 7 8 8" />
      <path d="m21 11-3-3" />
      <path d="m6 4-3 3" />
      <path d="M3 21h9" />
    </svg>
  ),
  Tv: () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="13" rx="2" />
      <path d="M7 21h10" />
      <path d="M12 18v3" />
    </svg>
  ),
};

// ── Data ──
const FEATURES = [
  {
    icon: 'Mic',
    title: 'Hears Every Bid',
    desc: 'Say "twenty" or "twenty dollars" — the app validates, formats, and posts the bid before the next breath. Misfires are filtered out so room chatter doesn\'t move the price.',
  },
  {
    icon: 'Timer',
    title: 'Cadence You Can Trust',
    desc: 'A five-second bid window, three-second closes, and a clear sold cut off. Any higher bid interrupts the call and restarts the clock — exactly like a real auctioneer.',
  },
  {
    icon: 'Gavel',
    title: 'Hands Off The Wheel',
    desc: 'The commissioner gets to focus on bidding. No more passing the gavel mid-round just because you wanted to chase a sleeper in the seventh.',
  },
  {
    icon: 'Tv',
    title: 'Readable Across The Room',
    desc: 'Designed for laptops, PCs, and TVs. Player, bid, timer, and state are sized so every drafter knows where the auction stands.',
  },
];

const STEPS = [
  { num: '01', title: 'Open The Bidding', desc: "Nominate a player or skip nomination to just use the consistent bidding windows. The board lights up, the mic opens, and the room knows it's live." },
  { num: '02', title: 'Call Your Number', desc: "Say your bid aloud — 'thirty' or 'thirty dollars.' Single digits are treated as a raise on the current bid (e.g., say '3' when the bid is $30 to bid $33). Only higher bids count. Each valid bid resets the clock." },
  { num: '03', title: 'Sold To The Room', desc: "Going once, going twice, sold. The bid locks and displays the player and final price. Owners who like to track sold prices never have to ask \"How much?\" again. The next nomination is one tap away." },
];

const CLIPS = [
  { cue: 'Cue One', num: '01', line: 'Place your opening bid.', file: 'place-your-opening-bid.mp3' },
  { cue: 'Cue Two', num: '02', line: 'Going once…', file: 'going-once.mp3' },
  { cue: 'Cue Three', num: '03', line: 'Going twice…', file: 'going-twice.mp3' },
  { cue: 'Cue Four', num: '04', line: <><em>Sold!</em></>, file: 'sold.mp3' },
];

const FAQ_ITEMS = [
  { q: 'Do I need to install anything?', a: "No. Fantasy Auctioneer runs in Chrome, Edge, or Firefox on any laptop or PC. Cast or mirror to a TV when the league's in the room — so every drafter can follow the nominated player, current bid, and bidding window." },
  { q: 'How do I display this on my TV?', a: 'The best way to connect a laptop to a TV is by connecting an HDMI cable. There are other casting options depending on your TV and streaming hardware.' },
  { q: 'How does the spoken-bid recognition actually work?', a: "Your browser's built-in speech recognition listens for numeric bids during the open window. Bids like \"twenty\" or \"twenty dollars\" are accepted. Single digits are treated as a raise on the current bid (e.g., say '3' when the bid is $30 to bid $33); small talk, noise, and lower bids are ignored." },
  { q: 'Can anyone else access and listen to my recordings?', a: 'No. All audio processing is done on the device and no recordings are stored on our servers.' },
  { q: 'Can I run this on phones or tablets?', a: "Not yet! Web apps that try to use the microphone and speaker the way Fantasy Auctioneer needs to don't work well on mobile browsers. Development of native mobile apps and smart TV apps is in the roadmap, so stay tuned!" },
  { q: 'Is there a fee?', a: 'No accounts, no fee, no signup. The product is free to use as we work out the bugs and continue to add features. There may be a cost to use Fantasy Auctioneer down the road, but right now we just want to get it out to the people.' },
];

function LandingPage() {
  const [playingClip, setPlayingClip] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const audioRef = useRef(null);

  /** Gate auction entry — show the mobile modal or navigate to /auction */
  const handleAuctionClick = (e) => {
    if (isMobileDevice()) {
      e.preventDefault();
      setShowMobileModal(true);
    }
  };

  const playClip = (index) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    if (playingClip === index) {
      setPlayingClip(null);
      return;
    }

    const audio = new Audio(`${process.env.PUBLIC_URL}/assets/audio/${CLIPS[index].file}`);
    audioRef.current = audio;
    audio.play().catch(() => {});
    setPlayingClip(index);
    audio.addEventListener('ended', () => {
      if (audioRef.current === audio) {
        setPlayingClip(null);
        audioRef.current = null;
      }
    });
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  return (
    <div className="landing-shell" style={{ '--hero-bg-url': HERO_BG }}>

      {/* ── Hero ── */}
      <header className="fa-hero">
        <div className="fa-hero__backdrop"></div>
        <div className="fa-hero__glow"></div>

        <nav className="fa-nav" aria-label="Primary">
          <button className="fa-nav__brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} type="button">
            <span className="fa-nav__mark" aria-hidden="true"></span>
            <span className="fa-nav__wordmark">Fantasy Auctioneer</span>
          </button>
          <div className="fa-nav__links">
            <a href="#features" className="fa-nav__link">Features</a>
            <a href="#how" className="fa-nav__link">How It Works</a>
            <a href="#voice" className="fa-nav__link">Listen</a>
            <a href="#faq" className="fa-nav__link">FAQ</a>
          </div>
          <Link to="/auction" className="fa-nav__cta" onClick={handleAuctionClick}>Start Auction</Link>
        </nav>

        <div className="fa-hero__inner">
          <div className="fa-hero__copy">
            <div className="fa-eyebrow-line">
              <span className="fa-eyebrow-line__pip"></span>
              <span>Live Draft Bidding &middot; For Laptop, PC &amp; TV</span>
              <span className="fa-eyebrow-line__rule"></span>
            </div>

            <h1 className="fa-hero__title">
              Give Your Draft a Voice.<br />
              <em>Let It Run The Auction.</em>
            </h1>

            <p className="fa-hero__tagline">
              Fantasy Auctioneer listens for spoken bids, calls a clean Going Once / Going Twice / Sold cadence,
              and keeps the whole room in sync &mdash; and the commissioner can focus on chasing those league winners.
            </p>

            <div className="fa-hero__cta-row">
              <Link to="/auction" className="fa-btn-primary" onClick={handleAuctionClick}>
                Start An Auction
                <span className="fa-btn-primary__chev" aria-hidden="true"></span>
              </Link>
              <a href="#voice" className="fa-btn-ghost">
                <span className="fa-btn-ghost__play" aria-hidden="true">&#9654;</span>
                Hear The Auctioneer
              </a>
            </div>

            <ul className="fa-hero__assurances">
              <li>No Account</li>
              <li>Runs In Your Browser</li>
              <li>Mic Stays Local</li>
            </ul>
          </div>

          <div className="fa-hero__demo">
            <MockAuctionBoard />
          </div>
        </div>
      </header>

      {/* ── Features ── */}
      <section id="features" className="fa-section">
        <div className="fa-section__head">
          <div className="fa-section__eyebrow">Built For The Room</div>
          <h2 className="fa-section__title">A Live Auction Board, <em>Not A Dashboard.</em></h2>
          <p className="fa-section__lede">
            Every surface is tuned for the energy of a live draft &mdash;
            confident, readable, and out of your way once the bidding starts.
          </p>
        </div>

        <div className="fa-features__grid">
          {FEATURES.map((f, i) => {
            const IconCmp = Icon[f.icon];
            return (
              <article className="fa-feature" key={f.title}>
                <div className="fa-feature__num">No. {String(i + 1).padStart(2, '0')}</div>
                <div className="fa-feature__icon" aria-hidden="true"><IconCmp /></div>
                <h3 className="fa-feature__title">{f.title}</h3>
                <p className="fa-feature__desc">{f.desc}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how" className="fa-section">
        <div className="fa-section__head">
          <div className="fa-section__eyebrow">The Cadence</div>
          <h2 className="fa-section__title">Every Auction, <em>Same Rhythm.</em></h2>
          <p className="fa-section__lede">
            Every nomination follows the same broadcast-tight rhythm &mdash;
            so the room learns the cues and the draft moves.
          </p>
        </div>

        <ol className="fa-playbill">
          {STEPS.map((s) => (
            <li className="fa-step" key={s.num}>
              <div className="fa-step__num" aria-hidden="true">
                <span className="fa-step__num-eyebrow">Step</span>
                <span className="fa-step__num-figure">{s.num}</span>
              </div>
              <div className="fa-step__body">
                <h3 className="fa-step__title">{s.title}</h3>
                <p className="fa-step__desc">{s.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ── Voice Clips ── */}
      <section id="voice" className="fa-voice">
        <div className="fa-voice__inner">
          <div className="fa-section__head" style={{ marginBottom: 24 }}>
            <div className="fa-section__eyebrow">The Voice Of The House</div>
            <h2 className="fa-section__title">Four Cues. <em>One Confident Voice.</em></h2>
            <p className="fa-section__lede">Tap a card to preview the auctioneer.</p>
          </div>

          <div className="fa-voice__grid">
            {CLIPS.map((c, i) => (
              <button
                key={c.num}
                className="fa-clip"
                data-playing={playingClip === i}
                onClick={() => playClip(i)}
              >
                <div className="fa-clip__cue">
                  <span>{c.cue}</span>
                  <span className="fa-clip__cue-num">{c.num}</span>
                </div>
                <h3 className="fa-clip__line">{c.line}</h3>
                <div className="fa-clip__waveform" aria-hidden="true">
                  {Array.from({ length: 22 }).map((_, j) => (
                    <span
                      key={j}
                      style={{
                        height: `${20 + Math.abs(Math.sin((i + 1) * (j + 1) * 0.7)) * 70}%`,
                      }}
                    />
                  ))}
                </div>
                <div className="fa-clip__play">
                  <span className="fa-clip__play-icon" aria-hidden="true"></span>
                  {playingClip === i ? 'Playing' : 'Preview Cue'}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="fa-section">
        <div className="fa-section__head">
          <div className="fa-section__eyebrow">Common Questions</div>
          <h2 className="fa-section__title">Before You <em>Place Your Bids.</em></h2>
        </div>

        <div className="fa-faq">
          {FAQ_ITEMS.map((item, i) => (
            <div className="fa-faq__item" key={item.q} data-open={openFaq === i}>
              <button className="fa-faq__q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                <span>{item.q}</span>
                <span className="fa-faq__chev" aria-hidden="true"></span>
              </button>
              <p className="fa-faq__a">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Finale CTA ── */}
      <section id="start" className="fa-finale">
        <div className="fa-finale__poster">
          <div className="fa-finale__eyebrow">Open The Floor</div>
          <h2 className="fa-finale__title">
            The Gavel Is <em>Yours.</em>
          </h2>
          <p className="fa-finale__lede">
            Pick a name, set the opening bid, and the cadence takes over.
            The auctioneer handles the rest.
          </p>
          <div className="fa-finale__divider">Let&#8217;s go</div>
          <div className="fa-finale__cta-row">
            <Link
              to="/auction"
              className="fa-btn-primary"
              onClick={handleAuctionClick}
              style={{
                background: 'linear-gradient(180deg, #2a1d0e 0%, #14090a 100%)',
                color: '#faf3e6',
                border: '1px solid rgba(255,244,223,0.18)',
              }}
            >
              Start An Auction
              <span className="fa-btn-primary__chev" aria-hidden="true"></span>
            </Link>
            <a
              href="mailto:myfantasyauctioneer@gmail.com?subject=Fantasy%20Auctioneer%20Feedback"
              className="fa-btn-ghost"
            >
              Send Feedback
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="fa-footer">
        <div className="fa-footer__row">
          <button className="fa-footer__brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} type="button">
            <span className="fa-nav__mark" aria-hidden="true"></span>
            Fantasy Auctioneer
          </button>
          <div className="fa-footer__links">
            <a href="#features">Features</a>
            <a href="#how">How It Works</a>
            <a href="#voice">Listen</a>
            <a href="#faq">FAQ</a>
          </div>
          <span>&copy; 2026 &middot; Built for draft day.</span>
        </div>
      </footer>

      <MobileBlockModal
        isOpen={showMobileModal}
        onDismiss={() => setShowMobileModal(false)}
      />
    </div>
  );
}

export default LandingPage;
